import { Link } from 'react-router-dom'
import { useLanguage } from '@/i18n/LanguageContext'
import { navItems } from './nav'
import { resolveNavHref } from './resolveNavHref'
import { LogoMark } from '@/components/ui/LogoMark'

/** New pages not in the primary nav, surfaced here instead — same pattern as Tehnostan's footer "Više" column. Grows as more SEO landing pages are added. */
const moreLinks: { href: string; labelBs: string; labelEn: string }[] = [
  { href: '/graficki-dizajn', labelBs: 'Grafički dizajn', labelEn: 'Graphic Design' },
  { href: '/print-materijali', labelBs: 'Print materijali', labelEn: 'Print Materials' },
  { href: '/izrada-web-stranica', labelBs: 'Izrada web stranica', labelEn: 'Web Design' },
  { href: '/cjenovnik', labelBs: 'Cjenovnik', labelEn: 'Pricing' },
]

/**
 * Real footer content migrated from the current live site's <footer> (index.html
 * lines ~1929-1966): footer nav (same anchors + privacy link), copyright with
 * dynamic year, Facebook/Instagram links, and featured client site cards
 * (tehnostan.online, mm-metal.com). Top corner treatment (rounded-t-xl)
 * matches the Stitch portfolio-page footer.
 */
export function SiteFooter() {
  const { t, lang } = useLanguage()
  const year = new Date().getFullYear()

  return (
    <footer className="rounded-t-xl border-t border-white/10 bg-background-elevated">
      <div className="max-w-container mx-auto px-6 md:px-10 py-10 md:py-12 grid gap-8 md:gap-10 grid-cols-1 md:grid-cols-4 md:items-start">
        <div className="flex flex-col items-center gap-4 text-center md:items-start md:text-left">
          <span className="flex items-center gap-2 font-serif text-lg text-foreground">
            <LogoMark className="h-7 w-7 shrink-0" />
            EA Studio
          </span>
          <nav aria-label="Footer navigacija" className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-foreground-muted md:justify-start">
            {navItems.map((item) => (
              <Link key={item.href} to={resolveNavHref(item.href, lang)} className="hover:text-accent-fire transition-colors">
                {t(item.labelKey)}
              </Link>
            ))}
            <Link to={resolveNavHref('/privacy', lang)} className="hover:text-accent-fire transition-colors">
              {t('footer-privacy')}
            </Link>
            <Link to={resolveNavHref('/terms', lang)} className="hover:text-accent-fire transition-colors">
              {t('terms-title')}
            </Link>
          </nav>
        </div>

        <div className="flex flex-col items-center gap-2 text-center md:items-start md:text-left">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
            {lang === 'en' ? 'More' : 'Više'}
          </span>
          {moreLinks.map((link) => (
            <Link key={link.href} to={resolveNavHref(link.href, lang)} className="text-sm text-foreground-muted hover:text-accent-fire transition-colors">
              {lang === 'en' ? link.labelEn : link.labelBs}
            </Link>
          ))}
        </div>

        <div className="flex flex-col items-center text-center gap-1 text-xs text-foreground-subtle">
          <span>© {year} EA Studio.</span>
          <span>{t('footer-rights')}</span>
        </div>

        <div className="flex flex-col items-center md:items-end gap-4">
          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61591545871243"
              target="_blank"
              rel="noopener"
              aria-label="Facebook"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-foreground-muted hover:text-accent-fire hover:border-accent-fire transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/ea.studio.official/"
              target="_blank"
              rel="noopener"
              aria-label="Instagram"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-foreground-muted hover:text-accent-fire hover:border-accent-fire transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
          </div>

          <div className="flex gap-3">
            <a
              href="https://tehnostan.online"
              target="_blank"
              rel="noopener"
              className="text-xs text-foreground-muted hover:text-accent-fire transition-colors underline underline-offset-4"
            >
              tehnostan.online
            </a>
            <a
              href="https://mm-metal.com"
              target="_blank"
              rel="noopener"
              className="text-xs text-foreground-muted hover:text-accent-fire transition-colors underline underline-offset-4"
            >
              mm-metal.com
            </a>
            <a
              href="https://namjestajjasarevic.org"
              target="_blank"
              rel="noopener"
              className="text-xs text-foreground-muted hover:text-accent-fire transition-colors underline underline-offset-4"
            >
              namjestajjasarevic.org
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
